import { useEffect, useState } from "react";

const STORAGE_KEY = "rivetai:last-project-location";
const LOCATION_CHANGED = "rivetai:last-project-location-changed";

/** The project page the user last had open in this tab, for "Back to <project>". */
export interface LastProjectLocation {
  workspaceId: string;
  projectId: string;
  projectName: string;
  path: string;
}

const isLastProjectLocation = (value: unknown): value is LastProjectLocation => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return ["workspaceId", "projectId", "projectName", "path"].every(
    (key) => typeof candidate[key] === "string" && candidate[key] !== ""
  );
};

export function readLastProjectLocation(): LastProjectLocation | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isLastProjectLocation(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeLastProjectLocation(location: LastProjectLocation): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(location));
  } catch {
    // Storage can be full or blocked; the back link then falls back to all projects.
  }
}

export function clearLastProjectLocation(projectId: string | number): void {
  try {
    if (readLastProjectLocation()?.projectId === String(projectId)) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      window.dispatchEvent(new Event(LOCATION_CHANGED));
    }
  } catch {
    // Storage may be unavailable in this browser.
  }
}

/** Remembers the open project page while the project sidebar is mounted. */
export function useRememberProjectLocation(location: LastProjectLocation | null): void {
  const serialized = location ? JSON.stringify(location) : null;
  useEffect(() => {
    if (!serialized) return;
    writeLastProjectLocation(JSON.parse(serialized) as LastProjectLocation);
  }, [serialized]);
}

/** The last project page of this tab, updated when that project is deleted. */
export function useLastProjectLocation(): LastProjectLocation | null {
  const [location, setLocation] = useState(readLastProjectLocation);
  useEffect(() => {
    const onLocationChanged = () => setLocation(readLastProjectLocation());
    window.addEventListener(LOCATION_CHANGED, onLocationChanged);
    return () => window.removeEventListener(LOCATION_CHANGED, onLocationChanged);
  }, []);
  return location;
}
